import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { ProductVisibilityType } from "@omni/types/api/product/bff/shared/enums/productVisibilityType.enum";
import { ProductSliderSources } from "@omni/types/components/product/enums/productSliderResource.enum";
import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";
import { useEffect, useRef, useState } from "react";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper/types";

import { SwiperNavButton } from "@/components/Product/ImageViewer/partials/SwiperNavButton/swiperNavButton.component";
import { ProductSlidersRenditions } from "@/components/Product/ProductImage/mappedRenditions";
import ProductTileComponent from "@/components/Product/ProductTile/productTile.component";
import { ProductTileLayout } from "@/components/Product/ProductTile/productTile.config";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { useProductCartContext } from "@/utils/hooks/cart.hook";
import * as SwiperUtils from "@/utils/swiper.utils";

import { useProductSliderContext } from "./productSlider.context";

const VisibilityTypeToFilter = new Set([
    ProductVisibilityType.Offline,
    ProductVisibilityType.NotAvailable,
    ProductVisibilityType.TemporaryNotAvailable
]);

const isVisible = (product: Product<ProductFieldSet.ProductTile>) =>
    !VisibilityTypeToFilter.has(product.productVisibilityType);

export type SkuProductSliderProps = {
    products: Array<Product<ProductFieldSet.ProductTile>>;
    deleteFromHistory?: any;
    deleteRecentsHistory?: any;
};

const ProductSliderComponent = ({
    products,
    deleteFromHistory,
    deleteRecentsHistory
}: SkuProductSliderProps) => {
    const { t } = useGranite();
    const [allowPrevious, setAllowPrevious] = useState(false);
    const [allowNext, setAllowNext] = useState(true);
    const [mainSwiper, setMainSwiper] = useState<SwiperCore>();
    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);
    const cartContext = useProductCartContext();

    const {
        source,
        title,
        breakpointsConfig,
        productTileConfig,
        analyticsContext
    } = useProductSliderContext();

    const productSourceIsRecent = ProductSliderSources.Recent === source;
    const spaceBetween = productSourceIsRecent ? 8 : 20;

    const variables = SwiperUtils.getLayoutCssVariables({
        layoutSettings: breakpointsConfig.layoutSettings,
        spaceBetween: spaceBetween
    });

    const breakPointSettings = SwiperUtils.getEffectiveLayoutSettings(
        breakpointsConfig.layoutSettings
    );

    useEffect(() => {
        if (mainSwiper) {
            setAllowPrevious(!mainSwiper.isBeginning);
            setAllowNext(!mainSwiper.isEnd);
        }
    }, [mainSwiper]);

    const updateButtons = () => {
        if (mainSwiper) {
            setAllowPrevious(!mainSwiper.isBeginning);
            setAllowNext(!mainSwiper.isEnd);
        }
    };

    return (
        <div
            className={cn("tw:pb-2", {
                "tw:max-lg:w-dvw tw:max-lg:relative tw:max-lg:left-1/2 tw:max-lg:right-1/2 tw:max-lg:mx-[-50vw] tw:max-lg:bg-grey tw:rounded-2xl":
                    productSourceIsRecent
            })}
        >
            <div
                className={cn({
                    "tw:bg-grey tw:p-4 tw:md:p-6 tw:xs:mt-6 tw:rounded-lg":
                        productSourceIsRecent
                })}
            >
                <div
                    className={cn(
                        "tw:flex tw:mb-2 tw:justify-between tw:w-full"
                    )}
                >
                    {title && (
                        <h2 className={cn("tw:mr-4 tw:my-2 tw:shrink")}>
                            {title}
                        </h2>
                    )}
                    {productSourceIsRecent && deleteRecentsHistory && (
                        <Button
                            color={"link"}
                            onClick={(e) => {
                                e.preventDefault();
                                deleteRecentsHistory();
                            }}
                        >
                            {t("productSlider.link.deleteHistory")}
                        </Button>
                    )}
                </div>
                <div
                    className={cn("tw:flex tw:items-center tw:w-full", {
                        "tw:justify-center":
                            productSourceIsRecent &&
                            !allowNext &&
                            !allowPrevious
                    })}
                >
                    <SwiperNavButton
                        innerRef={navigationPrevRef}
                        className={cn("tw:hidden tw:md:block", {
                            "tw:-translate-x-1/3": productSourceIsRecent
                        })}
                        disabled={!allowPrevious}
                        icon={"left"}
                    />
                    <Swiper
                        modules={[Navigation]}
                        onSwiper={setMainSwiper}
                        updateOnWindowResize={true}
                        resizeObserver={true}
                        onSlideChange={() => updateButtons()}
                        slidesPerView={1}
                        slidesPerGroup={1}
                        navigation={{
                            nextEl: navigationNextRef.current,
                            prevEl: navigationPrevRef.current
                        }}
                        wrapperClass={cn("tw:w-dvw tw:max-w-full tw:mr-auto")}
                        breakpoints={breakPointSettings}
                        spaceBetween={spaceBetween}
                        className={cn("tw:w-full tw:grow", {
                            "tw:[--slides-per-view:1]": !mainSwiper,
                            "tw:xs:[--slides-per-view:var(--slides-xs)]":
                                !mainSwiper,
                            "tw:sm:[--slides-per-view:var(--slides-sm)]":
                                !mainSwiper,
                            "tw:md:[--slides-per-view:var(--slides-md)]":
                                !mainSwiper,
                            "tw:lg:[--slides-per-view:var(--slides-lg)]":
                                !mainSwiper,
                            "tw:xl:[--slides-per-view:var(--slides-xl)]":
                                !mainSwiper
                        })}
                        style={variables}
                    >
                        {products
                            .filter(isVisible)
                            .map((product, index: number) => (
                                <SwiperSlide
                                    key={product.sku}
                                    className={cn("tw:flex tw:h-auto", {
                                        "tw:w-[calc((100%/var(--slides-per-view))-var(--slider-spacing)+(var(--slider-spacing)/(var(--slides-per-view))))] tw:mr-(--slider-spacing)":
                                            !mainSwiper
                                    })}
                                >
                                    <ProductTileComponent
                                        className={cn(
                                            "tw:w-full tw:mb-4 tw:rounded-md"
                                        )}
                                        product={product}
                                        cart={cartContext?.cart}
                                        allowInstantAddToCart={
                                            !!cartContext?.allowInstantAddToCart
                                        }
                                        productTileConfig={{
                                            amountOfFeatures:
                                                productTileConfig.amountOfFeatures,
                                            imageRenditions:
                                                ProductSlidersRenditions,
                                            border: false,
                                            tilePadding:
                                                productTileConfig.useInsidePadding ||
                                                productSourceIsRecent,
                                            components:
                                                ProductTileLayout.SLIDER(
                                                    productTileConfig.showButton,
                                                    productTileConfig.showCategoryLink,
                                                    productTileConfig.amountOfFeatures >
                                                        0
                                                )
                                        }}
                                        analyticsContext={
                                            analyticsContext && {
                                                ...analyticsContext,
                                                placement: index + 1
                                            }
                                        }
                                    >
                                        {productSourceIsRecent &&
                                            deleteFromHistory && (
                                                <Button
                                                    type={"button"}
                                                    className={
                                                        "tw:absolute tw:py-1 tw:px-2 tw:text-xl tw:z-1 tw:hidden tw:lg:block"
                                                    }
                                                    color={"transparent"}
                                                    aria-label={t(
                                                        "accessibility.productSlider.delete"
                                                    )}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        deleteFromHistory(
                                                            product.sku
                                                        );
                                                    }}
                                                >
                                                    <i
                                                        className={cn(
                                                            "las la-times"
                                                        )}
                                                    />
                                                </Button>
                                            )}
                                    </ProductTileComponent>
                                </SwiperSlide>
                            ))}
                    </Swiper>
                    <SwiperNavButton
                        innerRef={navigationNextRef}
                        className={cn("tw:hidden tw:md:block", {
                            "tw:translate-x-1/3": productSourceIsRecent
                        })}
                        disabled={!allowNext}
                        icon={"right"}
                    />
                </div>
            </div>
        </div>
    );
};

export { ProductSliderComponent };
